import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useDroppable } from '@dnd-kit/core';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import CollapsibleThumbnails from '../../../SharedMenuEditorPage/components/CollapsibleThumbnails/CollapsibleThumbnails';
import { Offer } from '../../../SharedMenuEditorPage/types';
import { MAX_OFFER_IMAGES_COUNT } from '../../constants';

type Props = {
  offer: Offer;
  primaryLanguage: Language;
  onDetachImage: (deleteAt: number) => Promise<void>;
};

const Root = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  height: 100%;
`;

const NameAndDescription = styled.div<{ isHidden?: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  white-space: nowrap;
  padding: 16px 0;
  opacity: ${({ isHidden }) => (isHidden ? 0 : 1)};
  transition: opacity 200ms ease-in-out;
`;

export const Name = styled(T.Body3).attrs({ fontWeight: 'bold' })`
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${cbSemanticCssVars.textPrimary};
`;

const Description = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ThumbnailContainer = styled.div`
  position: relative;
  margin-inline-end: 16px;
  z-index: 1;

  &:hover + ${NameAndDescription} {
    opacity: 0;
  }
`;

export const OfferRow = ({ offer, primaryLanguage, onDetachImage }: Props) => {
  const menuPermissions = useOfferingMenuPermissionsContext();

  const haxMaxImagesCountReached = offer.images && offer.images.length >= MAX_OFFER_IMAGES_COUNT;
  const canAddImages = !haxMaxImagesCountReached && menuPermissions.contentEdit;

  const { isOver: isDragOver, setNodeRef } = useDroppable({
    id: offer.id,
    data: {
      offerId: offer.id,
    },
    disabled: !canAddImages,
  });

  const thumbnails = useMemo(
    () =>
      offer.images && offer.images.length > 0
        ? offer.images
        : offer.image
          ? [{ url: offer.image ?? undefined, blurhash: offer.image_blurhash ?? undefined }]
          : [],
    [offer],
  );
  const offerName = localize(offer.name, primaryLanguage);

  const isThumbnailsExpanded = isDragOver && canAddImages;

  return (
    <Root ref={setNodeRef}>
      <ThumbnailContainer>
        <CollapsibleThumbnails
          isExpanded={isThumbnailsExpanded}
          images={thumbnails}
          imagesAlt={offerName}
          canDelete={menuPermissions.contentEdit}
          onDelete={onDetachImage}
        />
      </ThumbnailContainer>
      <NameAndDescription isHidden={isThumbnailsExpanded}>
        <Name dir="auto" lang={primaryLanguage}>
          {offerName}
        </Name>
        {offer.description && (
          <Description dir="auto" lang={primaryLanguage}>
            {localize(offer.description, primaryLanguage)}
          </Description>
        )}
      </NameAndDescription>
    </Root>
  );
};
